import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  adDensityForPath,
  adsAllowedNow,
  autoAdsAllowedNow,
  consentDecision,
  isBlockedPath,
  mainContentWordCount,
  pageIsNoindex,
  MIN_CONTENT_WORDS,
  MIN_LOW_CONTENT_WORDS,
} from "@/lib/ads-policy";
import { adsConfigSummary, AD_SLOTS, type AdVariant } from "@/lib/ads-config";

export const Route = createFileRoute("/admin/annonser")({
  head: () => ({
    meta: [
      { title: "Annonsdiagnostik — AK Husbil & Fritid" },
      { name: "description", content: "Internt verktyg för annonsstatus och placeringar." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdDiagnostics,
});

const PATHS = [
  "/",
  "/fordon",
  "/salda",
  "/fritid",
  "/reservdelar",
  "/blogg/exempel-inlagg",
  "/guider/exempel",
  "/campingtips/exempel",
  "/tester/exempel",
  "/ordlista/dubbelgolv",
  "/jamfor/exempel",
  "/forum",
  "/forum/t/exempel",
  "/fordon/nagon-husbil",
  "/kontakt",
  "/konto",
];

function AdDiagnostics() {
  const cfg = adsConfigSummary();
  const [live, setLive] = useState<{
    consent: string;
    words: number;
    noindex: boolean;
  } | null>(null);

  useEffect(() => {
    setLive({
      consent: consentDecision() ?? "inget beslut",
      words: mainContentWordCount(),
      noindex: pageIsNoindex(),
    });
  }, []);

  const variants: AdVariant[] = [
    "in-article",
    "in-article-2",
    "in-feed",
    "display",
    "display-horizontal",
    "mobile-fixed",
    "multiplex",
    "sticky",
  ];

  return (
    <div className="container-edge py-16" data-ads="off">
      <p className="eyebrow">Internt</p>
      <h1 className="mt-3 font-display text-4xl">Annonsdiagnostik</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Visar exakt vad annonssystemet gör just nu. Ingen annons renderas på den här sidan.
      </p>

      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl">Konfiguration</h2>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Master-brytare</dt>
            <dd className="font-medium">{cfg.enabled ? "PÅ" : "AV (VITE_ADSENSE_ENABLED)"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Läge</dt>
            <dd className="font-medium">{cfg.mode}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Auto Ads</dt>
            <dd className="font-medium">{cfg.autoAds ? "aktivt" : "av"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Manuella slots</dt>
            <dd className="font-medium">{cfg.manualAds ? "aktiva" : "av"}</dd>
          </div>
        </dl>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl">Slot-ID:n</h2>
        <ul className="mt-4 space-y-2 text-sm">
          {variants.map((v) => (
            <li key={v} className="flex items-center justify-between gap-4 border-b border-border/60 pb-2">
              <span className="font-mono text-xs">{v}</span>
              <span className={AD_SLOTS[v] ? "font-medium" : "text-muted-foreground"}>
                {AD_SLOTS[v] || "saknas"}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl">Den här sidan</h2>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Cookie-beslut</dt>
            <dd className="font-medium">{live?.consent ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Ord i innehållet</dt>
            <dd className="font-medium">{live?.words ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Noindex</dt>
            <dd className="font-medium">{live ? (live.noindex ? "ja" : "nej") : "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Ordkrav</dt>
            <dd className="font-medium">
              {MIN_LOW_CONTENT_WORDS} (låg) / {MIN_CONTENT_WORDS} (hög)
            </dd>
          </div>
        </dl>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl">Placeringsmatris</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2">Sida</th>
                <th className="py-2">Densitet</th>
                <th className="py-2">Blockerad</th>
                <th className="py-2">Auto Ads</th>
              </tr>
            </thead>
            <tbody>
              {PATHS.map((p) => (
                <tr key={p} className="border-b border-border/50">
                  <td className="py-2 font-mono text-xs">{p}</td>
                  <td className="py-2">{adDensityForPath(p)}</td>
                  <td className="py-2">{isBlockedPath(p) ? "ja" : "nej"}</td>
                  <td className="py-2">
                    {cfg.autoAds && adDensityForPath(p) === "high" ? "tillåtet" : "nej"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Kolumnerna visar policyregler. Slutgiltigt beslut tas i klienten per sidvisning
          (samtycke, ordräkning, noindex) — se {" "}
          <code className="font-mono">adsAllowedNow()</code> och{" "}
          <code className="font-mono">autoAdsAllowedNow()</code>. Aktuell sida:{" "}
          {typeof window !== "undefined" && adsAllowedNow(window.location.pathname)
            ? "annonser tillåtna"
            : "annonser blockerade"}
          {typeof window !== "undefined" && autoAdsAllowedNow(window.location.pathname)
            ? " (Auto Ads på)"
            : ""}
          .
        </p>
      </section>
    </div>
  );
}
